import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BravosWordmark } from '../components/BravosWordmark';
import { usePageStatusBar } from '../hooks/usePageStatusBar';
import { supabase } from '../lib/supabase';
import { ACCOUNT_ANDROID_PLAY_STORE_URL, ACCOUNT_IOS_APP_STORE_URL } from './account/accountSettingsContent';
import { stashMemberInviteCode } from '../lib/memberInviteAttribution';

/** iOS `BravosPageColor` / `AuthPalette.pageBackground` */
const PAGE_BG = 'var(--bg-page)';

/** Matched store badges (same 120×40 SVG). Shared height keeps them equal. */
const storeBadgeClass = 'h-12 w-auto object-contain sm:h-14';
const storeBadgeLinkClass =
  'inline-flex h-12 items-center justify-center transition opacity-95 hover:opacity-100 sm:h-14';

type StorePlatform = 'ios' | 'android' | 'desktop';

function detectStorePlatform(): StorePlatform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  // Only match real iOS devices in the UA. Do NOT use MacIntel + maxTouchPoints —
  // MacBook trackpads often report maxTouchPoints > 1 and falsely look like iPad.
  if (/iPhone|iPod|iPad/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

function AppStoreBadge({ href, ready }: { href: string; ready: boolean }) {
  const img = (
    <img
      src={`${process.env.PUBLIC_URL || ''}/store-badges/app-store-badge.svg`}
      alt="Download on the App Store"
      className={storeBadgeClass}
      width={120}
      height={40}
    />
  );
  if (ready) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={storeBadgeLinkClass}
        aria-label="Download on the App Store"
      >
        {img}
      </a>
    );
  }
  return <div className={`${storeBadgeLinkClass} opacity-55`}>{img}</div>;
}

function PlayStoreBadge({ href, ready }: { href: string; ready: boolean }) {
  const img = (
    <img
      src={`${process.env.PUBLIC_URL || ''}/store-badges/google-play-badge.svg`}
      alt="Get it on Google Play"
      className={storeBadgeClass}
      width={120}
      height={40}
    />
  );
  if (ready) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={storeBadgeLinkClass}
        aria-label="Get it on Google Play"
      >
        {img}
      </a>
    );
  }
  return <div className={`${storeBadgeLinkClass} opacity-55`}>{img}</div>;
}

/**
 * Public invite landing at `/invite/:code`.
 * Logs a click best-effort, then shows store CTAs (PRD member-invite-share-links-v1).
 * Mobile: only the matching store. Desktop: both.
 */
export function InviteLandingPage() {
  usePageStatusBar({ background: PAGE_BG });
  const { code } = useParams<{ code: string }>();
  const [platform, setPlatform] = useState<StorePlatform>('desktop');

  useEffect(() => {
    setPlatform(detectStorePlatform());
  }, []);

  // Keep personal invite URLs out of search / AI indexes (meta + robots.txt Disallow: /invite/).
  useEffect(() => {
    const robotsContent = 'noindex, nofollow, noarchive, nosnippet, noai, noimageai';
    const ensureMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };
    const robots = ensureMeta('robots', robotsContent);
    const googlebot = ensureMeta('googlebot', 'noindex, nofollow, noarchive, nosnippet');
    return () => {
      robots.remove();
      googlebot.remove();
    };
  }, []);

  useEffect(() => {
    const trimmed = (code || '').trim();
    if (!trimmed) return;
    stashMemberInviteCode(trimmed);
    let cancelled = false;
    void (async () => {
      try {
        await supabase.rpc('record_member_invite_click', {
          p_code: trimmed,
          p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 512) : null,
        });
      } catch {
        /* best-effort — never block landing */
      }
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const iosUrl = ACCOUNT_IOS_APP_STORE_URL;
  const iosReady = iosUrl.length > 0;
  const androidUrl = ACCOUNT_ANDROID_PLAY_STORE_URL;
  const androidReady = androidUrl.length > 0;

  const showIos = platform === 'ios' || platform === 'desktop';
  const showAndroid = platform === 'android' || platform === 'desktop';

  return (
    <div
      className="relative min-h-screen font-bravos antialiased text-theme-text"
      style={{ backgroundColor: PAGE_BG }}
    >
      <div className="relative z-10 flex min-h-screen flex-col">
        <header
          className="relative z-50 w-full shrink-0"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 sm:py-4">
            <Link to="/" className="shrink-0" aria-label="Bravos home">
              <BravosWordmark className="h-9 w-auto sm:h-11" />
            </Link>
          </div>
        </header>

        <main className="flex flex-1 flex-col px-6 pb-10 pt-8 sm:px-10 lg:px-12">
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
            <h1 className="font-pageTitle text-center text-2xl font-semibold tracking-tight text-theme-text sm:text-[1.5rem]">
              You’ve been invited to Bravos
            </h1>
            <p className="mt-3 text-center text-sm leading-relaxed text-theme-text sm:text-base">
              Train with a coach or discover workouts on your own. Download the app to get started.
            </p>

            <div className="mt-10">
              <p className="mb-4 text-center text-xs font-medium uppercase tracking-wider text-theme-text">
                Download the app
              </p>

              <div
                className={
                  showIos && showAndroid
                    ? 'flex w-full flex-row items-start justify-center gap-3'
                    : 'flex w-full flex-col items-center gap-3'
                }
              >
                {showIos ? (
                  <div className="flex flex-col items-center">
                    <AppStoreBadge href={iosUrl} ready={iosReady} />
                    {/* Keep row aligned when Play shows “Coming Soon”. */}
                    {showAndroid && !androidReady ? (
                      <span className="invisible mt-2 text-xs font-medium" aria-hidden>
                        Coming Soon
                      </span>
                    ) : null}
                  </div>
                ) : null}

                {showAndroid ? (
                  <div className="flex flex-col items-center">
                    <PlayStoreBadge href={androidUrl} ready={androidReady} />
                    {!androidReady ? (
                      <span className="mt-2 text-xs font-medium text-theme-text">Coming Soon</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <p className="mt-8 text-center text-base text-theme-text">
              Already have an account?{' '}
              <Link
                to="/auth?mode=login"
                className="font-semibold text-[var(--ink)] underline underline-offset-2 hover:opacity-90"
              >
                Sign in
              </Link>
            </p>
          </div>
        </main>

        <footer
          className="relative z-40 shrink-0"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto flex max-w-7xl items-end justify-between gap-4 px-4 pb-3 pt-2 sm:px-6 sm:pb-4 sm:pt-2">
            <nav
              className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-left text-xs font-normal text-theme-text sm:gap-x-5 sm:text-sm"
              aria-label="Legal"
            >
              <Link to="/terms" className="transition hover:opacity-80">
                T&Cs
              </Link>
              <Link to="/privacy" className="transition hover:opacity-80">
                Privacy Policy
              </Link>
            </nav>
            <p className="pointer-events-none shrink-0 text-right text-xs font-normal text-theme-text sm:text-sm">
              © 2026 Bravos
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default InviteLandingPage;
